import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CdkDragDrop, CdkDragEnd, CdkDragStart, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzMessageService } from 'ng-zorro-antd/message';
import { catchError, finalize, map, of, switchMap, timeout } from 'rxjs';

import { InboxTaskService } from '@core/services/api/inbox-task.service';
import { SortableService } from '@core/services/api/sortable.service';
import { SORTABLE } from '@app/constants/sortable.constants';
import { IEBInboxTask } from '@shared/models/entities/inbox-task.interface';
import { DEFAULT_PRIORITY, getPriorityIcon, getPriorityLabel, Priority } from '@shared/models/priority.enum';
import { resolveTaskStatus, TaskStatus } from '@shared/models/task-status.enum';
import { applySortableOrder } from '@shared/utils/apply-sortable-order';
import { withMinimumLoadingTime } from '@shared/utility/loading.operators';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { TranslateService } from '@core/services/translate.service';
import { UtilityDockStore } from '@app/shell/utility-dock/utility-dock.store';

@Component({
  selector: 'app-private-task-rail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzSkeletonModule,
    NzEmptyModule,
    TranslatePipe,
  ],
  templateUrl: './private-task-rail.component.html',
  styleUrls: ['./private-task-rail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrivateTaskRailComponent {
  private readonly inboxTaskApi = inject(InboxTaskService);
  private readonly sortable = inject(SortableService);
  private readonly router = inject(Router);
  private readonly utilityDockStore = inject(UtilityDockStore);
  private readonly message = inject(NzMessageService);
  private readonly translate = inject(TranslateService);

  readonly tasks = signal<IEBInboxTask[]>([]);
  readonly loading = signal(true);
  readonly creating = signal(false);
  readonly ordering = signal(false);
  readonly draggingTaskId = signal<string | null>(null);
  readonly dragStartDelay = { mouse: 50, touch: 140 };
  newTitle = '';
  private suppressOpenUntil = 0;

  constructor() {
    this.refresh();
  }

  refresh(): void {
    this.loading.set(true);
    this.inboxTaskApi.list({ status: TaskStatus.IN_PROGRESS, limit: 25 }, { suppressLoading: true })
      .pipe(
        timeout(8000),
        withMinimumLoadingTime({ minimumMs: 220 }),
        catchError(() => of([] as IEBInboxTask[])),
        switchMap((tasks) => {
          const privateTasks = (tasks ?? []).filter((task) => this.isPrivateTask(task));
          if (privateTasks.length <= 1) {
            return of(privateTasks);
          }

          return this.sortable
            .getOrder(SORTABLE.ENTITY_TYPE.INBOX_TASK, { scopeType: SORTABLE.SCOPE_TYPE.USER })
            .pipe(
              map((result) =>
                applySortableOrder(privateTasks, result?.orderedIds ?? [], (task) => task.inboxTaskId),
              ),
              catchError(() => of(privateTasks)),
            );
        }),
        finalize(() => this.loading.set(false)),
      )
      .subscribe((tasks) => {
        this.tasks.set(tasks ?? []);
      });
  }

  createTask(): void {
    const title = this.newTitle.trim();
    if (!title) {
      return;
    }

    this.creating.set(true);
    this.inboxTaskApi.create({ title }, { suppressLoading: true })
      .pipe(finalize(() => this.creating.set(false)))
      .subscribe({
        next: (created) => {
          const nextTasks = [
            created,
            ...this.tasks().filter((task) => task.inboxTaskId !== created.inboxTaskId),
          ];
          this.tasks.set(nextTasks);
          this.persistOrder(nextTasks);
          this.newTitle = '';
        },
        error: () => void 0,
      });
  }

  openTask(task: IEBInboxTask): void {
    if (Date.now() < this.suppressOpenUntil) {
      return;
    }

    this.utilityDockStore.closePrivateTasks();
    this.router.navigate(['/tasks/private', task.inboxTaskId], {
      state: { privateTask: task },
    });
  }

  trackTask(_index: number, task: IEBInboxTask): string {
    return task.inboxTaskId;
  }

  drop(event: CdkDragDrop<IEBInboxTask[]>): void {
    if (this.ordering() || event.previousIndex === event.currentIndex) {
      return;
    }

    const beforeItems = [...this.tasks()];
    const nextItems = [...beforeItems];
    moveItemInArray(nextItems, event.previousIndex, event.currentIndex);

    this.tasks.set(nextItems);
    this.ordering.set(true);

    this.sortable
      .updateOrder(SORTABLE.ENTITY_TYPE.INBOX_TASK, {
        scopeType: SORTABLE.SCOPE_TYPE.USER,
        orderedIds: nextItems.map((task) => task.inboxTaskId),
      })
      .pipe(finalize(() => this.ordering.set(false)))
      .subscribe({
        error: () => {
          this.tasks.set(beforeItems);
          this.message.error(this.translate.instant('privateTask.errors.persist_order_failed'));
        },
      });
  }

    private persistOrder(items: IEBInboxTask[]): void {
      const orderedIds = (items ?? []).map((task) => task.inboxTaskId).filter(Boolean);
      if (orderedIds.length !== (items ?? []).length || orderedIds.length <= 1) {
        return;
      }

      this.ordering.set(true);

      this.sortable
        .updateOrder(SORTABLE.ENTITY_TYPE.INBOX_TASK, {
          scopeType: SORTABLE.SCOPE_TYPE.USER,
          orderedIds,
        })
        .pipe(
          catchError(() => {
            this.message.error(this.translate.instant('privateTask.errors.persist_order_failed'));
            return of(null);
          }),
          finalize(() => this.ordering.set(false)),
        )
        .subscribe();
    }

  onDragStarted(task: IEBInboxTask, _event: CdkDragStart<IEBInboxTask>): void {
    this.draggingTaskId.set(task.inboxTaskId);
  }

  onDragEnded(_event: CdkDragEnd<IEBInboxTask>): void {
    this.draggingTaskId.set(null);
    this.suppressOpenUntil = Date.now() + 180;
  }

  priorityLabel(task: IEBInboxTask): string {
    return getPriorityLabel(task.priority);
  }

  priorityIcon(task: IEBInboxTask): string | null {
    return getPriorityIcon(task.priority);
  }

  protected showPriority(task: IEBInboxTask): boolean {
    const priority = (task.priority as Priority) ?? DEFAULT_PRIORITY;
    return priority === Priority.HIGH || priority === Priority.CRITICAL;
  }

  protected priorityTone(_task: IEBInboxTask): 'destructive' {
    return 'destructive';
  }

  isCompleted(task: IEBInboxTask | null | undefined): boolean {
    return resolveTaskStatus(task?.status, TaskStatus.IN_PROGRESS) === TaskStatus.DONE;
  }

  private isPrivateTask(task: IEBInboxTask | null | undefined): boolean {
    const kind = String(task?.kind ?? '').trim().toLowerCase();
    if (kind) {
      return kind === 'private';
    }

    const hasNotificationSource = String(task?.sourceNotificationId ?? '').trim().length > 0;
    const hasEntityContext = String(task?.entityId ?? '').trim().length > 0;
    return !hasNotificationSource && !hasEntityContext;
  }
}
